import { AgeRestriction } from '@diritto/common/content/constants';
import { InboxNotiDto } from '@diritto/common/notification/dtos/inbox-noti.dto';
import { Color } from '@diritto/common/style/color';
import { Typography } from '@diritto/common/style/typography';
import { convertToDotString, convertToRelativeString } from '@diritto/common/utils/date';
import styled from '@emotion/styled';
import { ArrowHorizontalIcon } from 'icons/ArrowHorizontalIcon';
import * as Styled from 'app-components/inbox/items/styles';
import { getTextLineStyle } from 'styles/common';
import { Divider } from 'ui-components/common/Divider';
import { Link } from 'ui-components/common/Link';
import { IkImage } from 'ui-components/Imagekit/IkImage';
import { AdultBadgeIcon } from 'icons/badge/AdultBadgeIcon';
import { ImageTransformOptions, ImageTransformType } from '@diritto/common/imageKit/constants';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodePublicRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { useState } from 'react';

interface EpisodeInboxNotiItemProps {
  inboxNoti: InboxNotiDto;
  content: ContentPublicRelayDto;
  episodes: EpisodePublicRelayDto[];
}

export const EpisodeInboxNotiItem = ({ inboxNoti, content, episodes }: EpisodeInboxNotiItemProps) => {
  const [isRead] = useState(inboxNoti.isRead);

  return (
    <Link href={`contents/${content.id}`}>
      <Styled.Container>
        <Styled.LeftCoverSection>
          <Styled.Unread isRead={isRead} />
          <Styled.CoverWrapper>
            <IkImage
              transform={{
                path: content.cover.path,
                isPrivate: content.cover.isPrivate,
                transformation: ImageTransformOptions[ImageTransformType.Inbox],
              }}
              alt={`${inboxNoti.title} 표지`}
            />
          </Styled.CoverWrapper>
        </Styled.LeftCoverSection>
        <Styled.RightSection>
          <Styled.TitleWrapper>
            <Styled.SanitizeHTMLTitle
              html={`${inboxNoti.title}<span class="inbox-title-date" title="${convertToDotString(
                inboxNoti.sentAt,
              )}">${convertToRelativeString(inboxNoti.sentAt)}</span>`}
              options={{
                allowedTags: ['b', 'span'],
                allowedClasses: {
                  span: ['inbox-title-date'],
                },
                allowedAttributes: { span: ['title'] },
              }}
            />
          </Styled.TitleWrapper>
          <Styled.Extra>
            <StyledEpisodeList>
              {episodes.map(episode => (
                <Link key={episode.id} href={`contents/${content.id}/episodes/${episode.id}`}>
                  <StyledEpisodeTitle>
                    {episode.ageRestriction === AgeRestriction.Adult && <AdultBadgeIcon />}
                    <p>{episode.title}</p>
                    <ArrowHorizontalIcon direction="right" color={Color.Gray400} />
                  </StyledEpisodeTitle>
                </Link>
              ))}
            </StyledEpisodeList>
          </Styled.Extra>
        </Styled.RightSection>
        <Divider color={Color.Gray200} />
      </Styled.Container>
    </Link>
  );
};

const StyledEpisodeTitle = styled.div`
  width: 100%;
  height: 37px;
  background-color: ${Color.Gray100};
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  gap: 8px;

  & > p {
    flex-grow: 1;
    ${Typography.XXSmall}
    ${getTextLineStyle(1)};
    color: ${Color.Gray600};
  }
`;

const StyledEpisodeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
